import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// Initializes jQuery DataTables directly rather than going through the shared `datatable`
// controller, so this page renders DataTables' default chrome: no custom `dom`, no `language`
// overrides, no pagination reskin. Deliberate - it is how this table has always looked.
//
// The row-button click handler never disables the button, and a failed request shows an error
// toast carrying the server's `message`.
export default class extends Controller {
  static get targets() {
    return ["table"];
  }

  static get values() {
    return { source: String };
  }

  connect() {
    this.dataTable = $(this.tableTarget).dataTable({
      processing: true,
      serverSide: true,
      searchDelay: 500,
      order: [[0, "desc"]],
      ajax: { url: this.sourceValue },
      pageLength: 25,
      pagingType: "full_numbers",
      columns: [{ data: "actions" }, { data: "gallery" }, { data: "gift_level" }, { data: "summary" }],
    });

    this.bindSearchInput();
  }

  disconnect() {
    if ($.fn.DataTable.isDataTable(this.tableTarget)) {
      $(this.tableTarget).DataTable().destroy();
    }
  }

  // The same `galleries_organization` payload key is sent whether the button targets the plain
  // assign/unassign endpoint or the global-gallery ignore/un-ignore one; both read
  // `params[:galleries_organization]`.
  //
  // All four actions this button can hit put a `format.html` branch ahead of `format.json`, so
  // the Accept and X-Requested-With headers below are required: without them Rails takes the html
  // branch and answers with a redirect.
  onRowButtonClick(event) {
    const button = event.currentTarget;

    const method = (button.dataset.method || "").toLowerCase();
    const options = method === "delete" ? RequestHelper.defaultDeleteOptions() : RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    });
    options.body = JSON.stringify({
      galleries_organization: {
        // `.dataset` never coerces, unlike jQuery's `.data()` - these are numeric ids, so coerce
        // explicitly to keep the JSON payload shape identical to what the server expects.
        gallery_id: Number(button.dataset.galleryId),
        organization_id: Number(button.dataset.organizationId),
      },
    });

    fetch(button.dataset.url, options).then((response) => {
      if (!response.ok) {
        response.json().then((data) => this.onRowButtonError(data));
        return;
      }

      response.json().then((data) => this.onRowButtonSuccess(button, data, response.status));
    });
  }

  onRowButtonSuccess(button, data, status) {
    if (data.url.includes("ignore")) {
      if (status === 201) {
        button.classList.add("btn-primary");
        button.textContent = "+";
        button.dataset.method = "delete";
      } else {
        button.classList.remove("btn-primary");
        button.textContent = "-";
        button.dataset.method = "post";
      }
    } else {
      if (status === 201) {
        button.classList.remove("btn-primary");
        button.textContent = "-";
        button.dataset.method = "delete";
      } else {
        button.classList.add("btn-primary");
        button.textContent = "+";
        button.dataset.method = "post";
      }
    }

    button.dataset.url = data.url;
  }

  onRowButtonError(data) {
    window.ltAlert({
      message: data.message,
      type: "toast-error",
      duration: 0,
      lasso: false,
      escapeMarkup: false,
    });
  }

  // The bare `.off()` below is load-bearing, not stylistic. DataTables binds the search input
  // itself on `keyup.DT search.DT input.DT paste.DT cut.DT`, throttled by its own `searchDelay`
  // (500 here) - a second, always-live search path. Stripping every handler first is what kills
  // it; narrowing this to our own namespace leaves DataTables firing a competing search/draw
  // against the same serverSide endpoint on every keystroke.
  bindSearchInput() {
    $(this.element).
      find(".dataTables_filter input[type='search']").
      off().
      on("input.galleriesOrganizations", (event) => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          $(this.tableTarget).DataTable().search(event.currentTarget.value).draw();
        }, 500);
      });
  }
}
